/**
 * HeaderLinkEditor
 *
 * The custom link in the pre-header bar — a payment portal, a client login, an
 * external booking system. Staff-only; the endpoint refuses a customer session.
 *
 * Uses the shared `api/editor` client rather than a bare `fetch`. Everything
 * under /api/editor is auth-gated, and a fetch without the session token fails
 * in a way that looks like an empty panel rather than an error.
 *
 * Saving rebuilds the scaffold on every page, so `onLinkChanged` must refresh
 * the preview — an unrefreshed iframe shows the old bar and reads as a no-op.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link2, Check, AlertCircle, Loader2, Trash2 } from 'lucide-react';
import {
  getSiteOverrides,
  updatePreheaderLink,
  type PreheaderLink,
} from '../../api/editor';

// Mirrors the server's caps. The server is the authority — it re-checks and
// 400s — but a field that stops accepting input explains the limit better than
// an error after the fact.
const MAX_TEXT = 32;
const MAX_SHORT = 16;

interface HeaderLinkEditorProps {
  websiteId: string;
  onLinkChanged?: () => void;
}

const EMPTY: PreheaderLink = { text: '', shortText: '', url: '' };

export function HeaderLinkEditor({ websiteId, onLinkChanged }: HeaderLinkEditorProps) {
  const [saved, setSaved] = useState<PreheaderLink>(EMPTY);
  const [draft, setDraft] = useState<PreheaderLink>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getSiteOverrides(websiteId)
      .then((res) => {
        if (cancelled) return;
        const link = res.overrides?.preheaderLink ?? EMPTY;
        setSaved(link);
        setDraft(link);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Could not load the header link.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [websiteId]);

  const dirty =
    draft.text !== saved.text ||
    draft.shortText !== saved.shortText ||
    draft.url !== saved.url;

  // Both halves are required to render. Saying so before the save turns a
  // server 400 into something the form can answer for itself.
  const incomplete =
    (draft.text.trim() !== '' && draft.url.trim() === '') ||
    (draft.url.trim() !== '' && draft.text.trim() === '');

  const commit = useCallback(
    async (next: PreheaderLink) => {
      setSaving(true);
      setError(null);
      setSuccess(null);
      try {
        const res = await updatePreheaderLink(websiteId, next);
        setSaved(res.preheaderLink);
        setDraft(res.preheaderLink);
        setSuccess(
          res.preheaderLink.url ? 'Header link updated' : 'Header link removed'
        );
        setTimeout(() => setSuccess(null), 2500);
        onLinkChanged?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not save the header link.');
      } finally {
        setSaving(false);
      }
    },
    [websiteId, onLinkChanged]
  );

  if (loading) {
    return (
      <div className="px-4 py-3 border-t border-gray-100">
        <div className="h-4 w-28 bg-gray-100 rounded animate-pulse mb-3" />
        <div className="h-8 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-t border-gray-100">
      <div className="flex items-center gap-2 mb-1">
        <Link2 className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
          Header Link
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        An extra link in the top bar, beside the phone number — a payment portal
        or client login. Appears on every page.
      </p>

      <label className="block text-xs font-medium text-gray-600 mb-1">Link text</label>
      <input
        type="text"
        value={draft.text}
        maxLength={MAX_TEXT}
        onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
        placeholder="Pay My Bill"
        className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
      />

      <label className="block text-xs font-medium text-gray-600 mb-1 mt-3">
        Link address
      </label>
      <input
        type="url"
        value={draft.url}
        onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
        placeholder="https://pay.example.com"
        className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
      />

      <label className="block text-xs font-medium text-gray-600 mb-1 mt-3">
        Short label for phones{' '}
        <span className="font-normal text-gray-400">(optional)</span>
      </label>
      <input
        type="text"
        value={draft.shortText}
        maxLength={MAX_SHORT}
        onChange={(e) => setDraft((d) => ({ ...d, shortText: e.target.value }))}
        placeholder={draft.text || 'Pay'}
        className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
      />
      <p className="text-xs text-gray-400 mt-1">
        The bar is tight on a phone. Without this, the full text is used at every
        size.
      </p>

      {incomplete && (
        <p className="mt-3 text-xs text-amber-600 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          Both the text and the address are needed before the link appears.
        </p>
      )}

      {error && (
        <p className="mt-3 text-xs text-red-600 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          {error}
        </p>
      )}

      {success && (
        <p className="mt-3 text-xs text-green-700 flex items-center gap-1.5">
          <Check className="w-3.5 h-3.5" />
          {success}
        </p>
      )}

      <div className="flex items-center gap-2 mt-3">
        <button
          type="button"
          onClick={() => commit(draft)}
          disabled={!dirty || incomplete || saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {saving && <Loader2 className="w-3 h-3 animate-spin" />}
          Save
        </button>

        {saved.url && (
          <button
            type="button"
            onClick={() => commit(EMPTY)}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-red-600 disabled:opacity-40 transition-colors"
          >
            <Trash2 className="w-3 h-3" />
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
